import { Button, cn } from "@growth/ui";
import { ImagePlus, LoaderCircle, WandSparkles, X } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";

import { apiErrorMessage } from "../lib/api-error";
import { ACCEPTED_IMAGE_TYPES, uploadImage } from "../lib/media";

type Props = {
  label: string;
  hint?: string;
  value: string | null;
  onChange: (url: string | null) => void;
  /** Aspect ratio of the preview box, e.g. "1200 / 630" for OG images. */
  aspect?: string;
  /** When set, offers "Generate with AI" (returns the new image URL). */
  onGenerate?: () => Promise<string>;
};

export const ImageField = ({
  label,
  hint,
  value,
  onChange,
  aspect = "16 / 9",
  onGenerate,
}: Props) => {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!onGenerate) return;
    setGenerating(true);
    setError(null);
    try {
      onChange(await onGenerate());
    } catch (caught) {
      setError(apiErrorMessage(caught, "Image generation failed"));
    } finally {
      setGenerating(false);
    }
  };
  const [error, setError] = useState<string | null>(null);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      onChange((await uploadImage(file)).url);
    } catch (caught) {
      setError(apiErrorMessage(caught, caught instanceof Error ? caught.message : "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-ink">{label}</p>
      <div
        className={cn(
          "group relative overflow-hidden rounded-lg border border-line bg-paper/60",
          !value && "border-dashed",
        )}
        style={{ aspectRatio: aspect }}
      >
        {value ? (
          <>
            <img src={value} alt="" className="size-full object-cover" />
            <button
              type="button"
              aria-label={`Remove ${label.toLowerCase()}`}
              onClick={() => onChange(null)}
              className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-white/90 text-ink shadow-sm transition-opacity hover:bg-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <X className="size-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={uploading}
            className="flex size-full flex-col items-center justify-center gap-1.5 text-xs text-muted transition-colors hover:text-ink"
          >
            {uploading ? (
              <LoaderCircle className="size-5 animate-spin" />
            ) : (
              <ImagePlus className="size-5" />
            )}
            {uploading ? "Uploading…" : "Upload image"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {value && (
          <Button
            type="button"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={uploading || generating}
            onClick={() => input.current?.click()}
          >
            {uploading ? "Uploading…" : "Replace"}
          </Button>
        )}
        {onGenerate && (
          <Button
            type="button"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={uploading || generating}
            onClick={() => void generate()}
          >
            {generating ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : (
              <WandSparkles className="size-3.5" />
            )}
            {generating
              ? "Generating… (about 30 s)"
              : value
                ? "Regenerate with AI"
                : "Generate with AI"}
          </Button>
        )}
      </div>
      {(error ?? hint) && (
        <p className={cn("text-xs", error ? "text-red-600" : "text-muted")}>{error ?? hint}</p>
      )}
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
};
